import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { risksKeys } from "@/application/query-keys/risks.keys";
import {
  risksRepository,
  type RiskFilters,
} from "@/infrastructure/repositories/risks.supabase.repository";
import type { RiskRow } from "@/infrastructure/supabase/database.types";

export function useRisksList(filters: RiskFilters = {}) {
  return useQuery({
    queryKey: risksKeys.list(filters),
    queryFn: () => risksRepository.list(filters),
  });
}

export function useRiskDetail(id: string | undefined) {
  return useQuery({
    queryKey: risksKeys.detail(id ?? ""),
    queryFn: () => risksRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useUpdateRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<RiskRow> }) =>
      risksRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: risksKeys.list() });
      queryClient.setQueryData(risksKeys.detail(data.id), data);
    },
  });
}

export function useCreateRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<RiskRow, "id" | "created_at" | "updated_at">) =>
      risksRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: risksKeys.list() });
    },
  });
}
